import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';

import { Typography } from '@material-ui/core';

import { useFetch, useUser } from '../../../../shared';
import { Message } from '../../../../shared/components';
import apiRoutes from '../../../../../config/api/routes';
import { ExerciseSeriesType } from '../../../../../config/api/models/Exercise';
import AddSeriesFormContent from './AddSeriesFormContent';
import { useParams } from 'react-router';
import { LogActionTypes, useDataLogging } from '../../../../shared/data-logging';
import { getPageMapper } from '../../../../../config/api/models';
import { UrlParams } from '../../../../shared/types';
import { StyledWrapper } from '../Atoms';

type Props = {
  type: ExerciseSeriesType;
  onAddSeries: () => void;
};

const AddSeriesForm: FC<Props> = ({ type, onAddSeries }) => {
  const { isTeacher } = useUser();
  const log = useDataLogging();
  const { bookId, trajectoryId } = useParams<UrlParams>();
  const [addSeriesResponse, addSeries, resetAddSeries] = useFetch(
    apiRoutes.bookTrajectoryPages(bookId as string, trajectoryId as string),
    {
      method: 'POST',
      mapper: getPageMapper,
    }
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (addSeriesResponse.success) {
      if (isTeacher) {
        log(LogActionTypes.TEACHER_PAGE_CREATE, {
          bookId,
          trajectoryId,
          pageId: addSeriesResponse?.data?.id,
          type: addSeriesResponse?.data?.type,
        });
      }
      resetAddSeries();
      onAddSeries();
    }
  }, [addSeriesResponse, isTeacher, log, onAddSeries, bookId, trajectoryId, resetAddSeries]);

  return (
    <StyledWrapper>
      <Typography variant="h3">{t('trajectory.sections.shared.newSeries')}</Typography>
      <Form
        onSubmit={(values: any) => {
          addSeries({
            body: {
              title: values.title,
              type: type,
            },
          });
        }}
        initialValues={{
          title: '',
        }}
        render={({ handleSubmit, form }) => (
          <>
            <AddSeriesFormContent
              handleSubmit={(...args) => {
                handleSubmit(...args);
                form.reset();
                form.resetFieldState('title');
              }}
            />
            {addSeriesResponse && addSeriesResponse.error && (
              <Message type="error">{t(addSeriesResponse.reason)}</Message>
            )}
          </>
        )}
      />
    </StyledWrapper>
  );
};

export default AddSeriesForm;
